/**
 * Closing a postpaid period and moving an account to prepaid (docs/BILLING.md §3 in
 * the backend repo), as the portal drives it.
 *
 * <h3>Cut-offs are IST on screen, UTC on the wire</h3>
 * `from-usage?before=` and `convert-to-prepaid?asOf=` take a zone-less
 * LocalDateTime, compared against `credit_transactions.created_at`, which the JVM
 * stamps in UTC (`-Duser.timezone=UTC`). An operator thinks "close the books at
 * midnight on the 19th" in IST. Sending that wall time unconverted would cut the
 * period 5h30m late and sweep a morning of the next period into this invoice.
 */

/** Only these may invoice usage or convert an account; the backend says the same. */
const CLOSE_BOOKS_ROLES = ['platform_admin', 'master_admin'];

export const canCloseBooks = (role: string | null | undefined): boolean =>
  CLOSE_BOOKS_ROLES.includes((role || '').toLowerCase());

const IST_OFFSET_MINUTES = 330;

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * `2026-10-19T00:00` read as IST → `2026-10-18T18:30:00`, the server's UTC
 * LocalDateTime. Null for anything that is not a full date and time.
 */
export const istToServerUtc = (istLocal: string | null | undefined): string | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec((istLocal || '').trim());
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number);
  const utc = new Date(Date.UTC(y, mo - 1, d, h, mi) - IST_OFFSET_MINUTES * 60_000);
  if (Number.isNaN(utc.getTime())) return null;
  return `${utc.getUTCFullYear()}-${pad(utc.getUTCMonth() + 1)}-${pad(utc.getUTCDate())}`
    + `T${pad(utc.getUTCHours())}:${pad(utc.getUTCMinutes())}:00`;
};

/** Today's IST midnight as a `datetime-local` value: the usual cut-off. */
export const istMidnightToday = (now: Date = new Date()): string => {
  const ist = new Date(now.getTime() + IST_OFFSET_MINUTES * 60_000);
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}T00:00`;
};

/** Now on the IST wall clock, as a Date whose UTC fields read as IST. */
const istWall = (now: Date = new Date()): Date => new Date(now.getTime() + IST_OFFSET_MINUTES * 60_000);

/** Today in IST, `YYYY-MM-DD`. `toISOString()` gives the UTC date: yesterday until 05:30 IST. */
export const istToday = (now: Date = new Date()): string => {
  const ist = istWall(now);
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}`;
};

/** This month in IST, `YYYY-MM`. */
export const istMonth = (now: Date = new Date()): string => istToday(now).slice(0, 7);

/** Now in IST as a `datetime-local` value, `YYYY-MM-DDTHH:mm`. */
export const istNowLocal = (now: Date = new Date()): string => {
  const ist = istWall(now);
  return `${istToday(now)}T${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}`;
};

/** `YYYY-MM-DD` plus n calendar days, with no time zone involved. */
export const addDays = (ymd: string, n: number): string => {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
};

/**
 * The next IST midnight as a `datetime-local` value: the default "prepaid from". At
 * 15:30 IST on 31 Oct it is `2026-11-01T00:00`, sent as `2026-10-31T18:30:00` UTC.
 */
export const istNextMidnight = (now: Date = new Date()): string => `${addDays(istToday(now), 1)}T00:00`;

/** A server UTC LocalDateTime (`2026-09-30T18:30:00`) as an IST `datetime-local` value. */
export const serverUtcToIstLocal = (utc: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec((utc || '').trim());
  if (!m) return '';
  const [, y, mo, d, h, mi] = m.map(Number);
  return istNowLocal(new Date(Date.UTC(y, mo - 1, d, h, mi)));
};

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * IST days `fromYmd`..`toYmd` (both inclusive, either optional) as the half-open UTC
 * range `[from, to)` the server compares its UTC times against: 1 Oct IST starts at
 * 18:30 UTC on 30 Sep. Sending the bare dates would drop 00:00–05:30 IST of the first
 * day and add 00:00–05:30 IST of the day after the last.
 */
export const istDaysToServerUtc = (
  fromYmd?: string | null,
  toYmd?: string | null,
): { from?: string; to?: string } => {
  const range: { from?: string; to?: string } = {};
  if (fromYmd && YMD.test(fromYmd)) range.from = istToServerUtc(`${fromYmd}T00:00`) || undefined;
  if (toYmd && YMD.test(toYmd)) range.to = istToServerUtc(`${addDays(toYmd, 1)}T00:00`) || undefined;
  return range;
};

/** This IST month so far: the 1st to today, as IST days. On 1 Oct at 00:30 IST that is 1 Oct, not September. */
export const istMonthToDate = (now: Date = new Date()): { from: string; to: string } => ({
  from: `${istMonth(now)}-01`,
  to: istToday(now),
});

/** An IST day range from two date inputs: an end before the start is dropped, not sent. */
export const orderedDays = (from: string, to: string): { from: string; to: string } =>
  from && to && to < from ? { from, to: '' } : { from, to };

/**
 * A server UTC LocalDateTime (no zone) shown in IST, `13 Oct 2026, 10:21 am`.
 * `new Date('2026-10-13T04:51:00')` reads a zone-less time as the BROWSER's local time,
 * which showed UTC as if it were IST, 5h30m early.
 */
export const formatServerUtcAsIst = (utc?: string | null): string => {
  if (!utc) return '-';
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(utc) ? utc : `${utc}Z`);
  if (Number.isNaN(d.getTime())) return utc;
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/**
 * A calendar date (`YYYY-MM-DD`: invoice, due and period dates) as `13 Oct 2026`.
 * `new Date('2026-10-13')` is UTC midnight, which a browser west of UTC shows as the 12th.
 */
export const formatYmd = (ymd?: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '');
  if (!m) return ymd || '-';
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
    .toLocaleDateString('en-IN', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' });
};

/** The invoice statuses on which money is still owed and a payment can be recorded. */
const PAYABLE = ['PENDING', 'PARTIALLY_PAID', 'OVERDUE'];

export const isPayable = (inv: { status?: string; balanceDue?: number | null }): boolean =>
  PAYABLE.includes(inv.status || '') && (inv.balanceDue ?? 0) > 0;

/**
 * A usage invoice. The DTO carries `kind` once the backend sends it; until then the
 * notes `UsageInvoicing` always writes identify one.
 */
export const isUsageInvoice = (inv: { kind?: string | null; notes?: string | null }): boolean =>
  inv.kind ? inv.kind === 'USAGE' : /^Usage charged before /.test(inv.notes || '');

/**
 * Whether a convert may go ahead, from its preview. When only usage invoiced outside
 * cpass stands in the way, the operator may say so with a reference (required); that
 * marks the earlier usage settled in cpass and creates no cpass tax invoice.
 */
export const convertDecision = (
  preview: { canConvert: boolean; canConvertWithOutsideSettlement: boolean } | null | undefined,
  settledOutside: boolean,
  reference: string,
): { allowed: boolean; reference?: string; offerOutside: boolean; reason?: string } => {
  if (!preview) return { allowed: false, offerOutside: false, reason: 'Waiting for the preview' };
  if (preview.canConvert) return { allowed: true, offerOutside: false };
  if (!preview.canConvertWithOutsideSettlement) {
    return { allowed: false, offerOutside: false, reason: 'Invoice and settle the earlier usage first' };
  }
  const ref = (reference || '').trim();
  if (!settledOutside) return { allowed: false, offerOutside: true, reason: 'Earlier usage is not invoiced in cpass' };
  if (!ref) return { allowed: false, offerOutside: true, reason: 'Give the outside invoice reference' };
  return { allowed: true, offerOutside: true, reference: ref };
};
